// @flow

import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import equals from 'ramda/src/equals';
import omit from 'ramda/src/omit';
import path from 'ramda/src/path';
import camelCase from 'voca/camel_case';
import { compose, mapProps, pure, withProps, type HOC } from 'recompose';

export const VariationQuery = gql`
  query Variation($id: String!, $defaultValue: Boolean) {
    variation(id: $id, defaultValue: $defaultValue) {
      id
      value
    }
  }
`;

type QueryDataProp = { [dataID: string]: { variation?: { value: boolean } } };

type CreateVariationProp = (
  variationID: string,
  dataID: string,
  defaultValue?: boolean
) => (
  props: QueryDataProp
) => {|
  [propName: string]: boolean | void
|};

// Also exposes a `<propName>Loading` flag so callers that need to know
// whether the resolved value is still provisional (the defaultValue, not
// yet confirmed by the server) can wait instead of acting on it -- see
// ExpressionContext.js, which does exactly that.
export const toVariationProp: CreateVariationProp = (
  variationID,
  dataID,
  defaultValue
) => (props: QueryDataProp) => {
  const value = path([dataID, 'variation', 'value'], props);
  // `value` is `undefined` both while the query is loading and once it has
  // settled with no data (e.g. an error, or no matching mock in a test).
  // Only the former is really "loading" -- checking Apollo's own `loading`
  // field (when present) tells them apart; falling back to `value` alone
  // when it's absent covers synthetic props built without one, like in
  // Variation.test.js.
  const isLoading =
    value === undefined && path([dataID, 'loading'], props) !== false;
  const resolvedValue =
    value === undefined
      ? defaultValue === undefined
        ? undefined
        : equals(true, defaultValue)
      : equals(true, value);
  const propName = camelCase(variationID);

  return {
    [propName]: resolvedValue,
    [`${propName}Loading`]: isLoading
  };
};

export const withVariation = (variationID: string, defaultValue?: boolean) => {
  // `dataID` is needed so we don't omit the resulting `{ [propName: string]: boolean }`
  // prop if its camel-cased prop name happens to be identical to its variation id,
  // like in the case of variation `flowchart`.
  const dataID = `data-${variationID}`;

  const variationHOC: HOC<*, Object> = compose(
    // Poll for variation value
    graphql(VariationQuery, {
      name: dataID,
      options: {
        fetchPolicy: 'network-only',
        pollInterval: 1000 * 60 * 60, // 1 hour
        variables: { id: variationID, defaultValue }
      }
    }),
    // Create camel cased key/value pair as variation prop.
    withProps(toVariationProp(variationID, dataID, defaultValue)),
    // Omit nested data object from props.
    mapProps(omit([dataID])),
    // Important: Don't update component unless variation value changed otherwise
    // unneeded re-rendering may occur whereever feature flags are used.
    pure
  );

  return variationHOC;
};

export default withVariation;
